import { Component, type SyntheticEvent } from "react";
import { clone, equals, pick } from "ramda";
import { observer } from "mobx-react";
import {
    Stack,
    TextField,
    DefaultButton,
    Separator,
    getTheme,
    DatePicker,
    Toggle,
    PivotItem,
    Pivot,
} from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import {
    doubleBindNullableString,
    doubleBindNullableStringEmptyAsNull,
    doubleBindString,
} from "../../utils/double-bind";
import {
    ManagementRole,
    FleetRole,
    type UserCreate,
    type Department,
    type Shift,
    type Site,
    type User,
} from "../../api";
import { type FormCreateProps } from "../../utils/form-create-props";
import { createUuid, type UUID } from "../../utils/uuid";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ModalConfirmation } from "../atoms/modal-confirmation";
import SearchableDropdown from "../atoms/searchable-dropdown";
import { ClearableDropdown } from "../atoms/clearable-dropdown";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { ImageUploader } from "../atoms/image-uploader";
import { defaultPageSize } from "../../utils/constants";

const theme = getTheme();

export interface FormCreateUserProps extends FormCreateProps<User> {}
import { store } from "../../store";

@observer
export class FormCreateUser extends Component<FormCreateUserProps> {
    @observable public accessor employeeId = "";
    @observable public accessor firstName = "";
    @observable public accessor lastName = "";
    @observable public accessor password: string | undefined;
    @observable public accessor passwordRepeat: string | undefined;
    @observable public accessor email: string | undefined;
    @observable public accessor jobTitle: string | undefined;
    @observable public accessor departmentId: UUID | null | undefined;
    @observable public accessor shiftId: UUID | null | undefined;
    @observable public accessor siteId: UUID | null | undefined;
    @observable public accessor managementRole: ManagementRole | undefined;
    @observable public accessor fleetRole: FleetRole | undefined;
    @observable public accessor licenseExpiryDate: Date | undefined;
    @observable public accessor expiryLogout: boolean = false;
    @observable public accessor nfcToken: string | null | undefined = null;
    @observable public accessor nfcTokenDescription: string | null | undefined = null;
    @observable private accessor cancelConfirmationModalVisible = false;

    // Properties that aren't used during render and don't need to be observable
    private imageBytes: Uint8Array | undefined;
    private validationId = createUuid();
    private cleanState: UserCreate;

    constructor(props: FormCreateUserProps) {
        super(props);
        makeObservable(this);
        this.cleanState = clone(this.userCreate);
    }

    private triggerValidation(): void {
        store.repositoryUsers.validation.create.updateModel(this.validationId, this.userCreate);
    }

    componentDidMount(): void {
        store.repositoryUsers.validation.create.initializeModel(this.validationId, this.userCreate);
    }

    @computed private get userCreate(): UserCreate {
        return pick(
            [
                "email",
                "employeeId",
                "password",
                "firstName",
                "lastName",
                "jobTitle",
                "departmentId",
                "shiftId",
                "siteId",
                "managementRole",
                "fleetRole",
                "licenseExpiryDate",
                "expiryLogout",
                "nfcToken",
                "nfcTokenDescription",
            ],
            this as FormCreateUser,
        );
    }

    @action.bound
    private async createUser(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();

        const user = await store.repositoryUsers.create(this.userCreate, this.imageBytes);
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(user);
        }
    }

    @action.bound private reset(): void {
        this.employeeId = "";
        this.firstName = "";
        this.lastName = "";
        this.password = undefined;
        this.passwordRepeat = "";
        this.email = undefined;
        this.jobTitle = undefined;
        this.departmentId = undefined;
        this.shiftId = undefined;
        this.siteId = undefined;
        this.managementRole = undefined;
        this.fleetRole = undefined;
        this.licenseExpiryDate = undefined;
        this.expiryLogout = false;
        this.nfcToken = undefined;
        this.nfcTokenDescription = undefined;
        this.triggerValidation();
    }

    /**
     * This is run, if the user clicks the form's cancel button.
     * A confirmation modal might pop up, if the user did any changes.
     */
    @action.bound private showConfirmAndCallDialogCancelCallback(): void {
        // If there were any changes, i.e. if the original and mutable copy aren't the same,
        // open the confirmation modal for aborting the update process.
        if (!equals(this.cleanState, this.userCreate)) {
            this.cancelConfirmationModalVisible = true;
            return;
        }

        this.props.onDialogCancel();
    }

    @action.bound private closeCancelConfirmationModalVisible(): void {
        this.cancelConfirmationModalVisible = false;
    }

    @action.bound private setFleetRole(fleetRole?: FleetRole): void {
        this.fleetRole = fleetRole;
        this.triggerValidation();
    }

    @action.bound private setManagementRole(managementRole?: ManagementRole): void {
        this.managementRole = managementRole;
        this.triggerValidation();
    }

    @action.bound private setDepartment(departmentId?: UUID): void {
        this.departmentId = departmentId ?? null;
        this.triggerValidation();
    }

    @action.bound private setShift(shiftId?: UUID): void {
        this.shiftId = shiftId ?? null;
        this.triggerValidation();
    }

    @action.bound private setSite(siteId?: UUID): void {
        this.siteId = siteId ?? null;
        this.triggerValidation();
    }

    @action.bound private setLicenseExpiryDate(date: Date | null | undefined): void {
        this.licenseExpiryDate = date ?? undefined;
        this.triggerValidation();
    }

    @action.bound private setExpiryLogout(
        _event: unknown,
        expiryLogout: boolean | undefined,
    ): void {
        this.expiryLogout = expiryLogout ?? false;
        this.triggerValidation();
    }

    @action.bound private setImage(bytes?: Uint8Array): void {
        this.imageBytes = bytes;
    }

    @computed private get passwordRepeatValid(): boolean {
        return this.password === this.passwordRepeat;
    }

    @computed private get passwordRepeatErrorMessage(): string | undefined {
        if (this.passwordRepeatValid) {
            return;
        }
        return store.i18n.t("formCreateUser.passwordRepeatInvalid");
    }

    public onFormatDate = (date?: Date): string => {
        return store.i18n.formatDateOnly(date!);
    };

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreateUser.submit.text")}
                validation={store.repositoryUsers.validation.create}
                validationId={this.validationId}
                additionalCondition={this.passwordRepeatValid}
            />
        );
        return (
            <form onSubmit={this.createUser}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <Stack
                        horizontal
                        horizontalAlign="space-between"
                        tokens={{
                            childrenGap: "2em",
                        }}
                    >
                        <ImageUploader
                            onImageChanged={this.setImage}
                            label={store.i18n.t("formCreateUser.image.label")}
                            placeholderIcon="ContactCard"
                        />
                        <Stack>
                            <TextField
                                label={store.i18n.t("formCreateUser.employeeId.label")}
                                {...doubleBindString<FormCreateUser>(this, "employeeId", () =>
                                    this.triggerValidation(),
                                )}
                                required
                                errorMessage={store.i18n.formatFieldValidationState(
                                    store.repositoryUsers.validation.create.getFieldValidationState(
                                        this.validationId,
                                        "employeeId",
                                    ),
                                )}
                            />
                            <TextField
                                label={store.i18n.t("formCreateUser.lastName.label")}
                                {...doubleBindString<FormCreateUser>(this, "lastName", () =>
                                    this.triggerValidation(),
                                )}
                                required
                                errorMessage={store.i18n.formatFieldValidationState(
                                    store.repositoryUsers.validation.create.getFieldValidationState(
                                        this.validationId,
                                        "lastName",
                                    ),
                                )}
                            />
                            <TextField
                                label={store.i18n.t("formCreateUser.firstName.label")}
                                {...doubleBindString<FormCreateUser>(this, "firstName", () =>
                                    this.triggerValidation(),
                                )}
                                required
                                errorMessage={store.i18n.formatFieldValidationState(
                                    store.repositoryUsers.validation.create.getFieldValidationState(
                                        this.validationId,
                                        "firstName",
                                    ),
                                )}
                            />
                        </Stack>
                        <Stack>
                            <TextField
                                label={store.i18n.t("formCreateUser.jobTitle.label")}
                                {...doubleBindNullableString<FormCreateUser>(this, "jobTitle", () =>
                                    this.triggerValidation(),
                                )}
                                errorMessage={store.i18n.formatFieldValidationState(
                                    store.repositoryUsers.validation.create.getFieldValidationState(
                                        this.validationId,
                                        "jobTitle",
                                    ),
                                )}
                            />
                            <SearchableDropdown
                                formatEntity={(department: Department) =>
                                    store.i18n.formatDepartment(department)
                                }
                                repository={store.repositoryDepartments}
                                query={{ pageSize: defaultPageSize }}
                                label={store.i18n.t("formCreateUser.department.label")}
                                onChange={this.setDepartment}
                                selectedKey={this.departmentId}
                                errorMessage={store.i18n.formatFieldValidationState(
                                    store.repositoryUsers.validation.create.getFieldValidationState(
                                        this.validationId,
                                        "departmentId",
                                    ),
                                )}
                                clearable
                            />
                        </Stack>
                    </Stack>
                    <Separator></Separator>
                    <Stack
                        horizontal
                        horizontalAlign="space-between"
                        tokens={{
                            childrenGap: "2em",
                        }}
                        // When the Current Tab is changed, the Height of the Current Form also changes,
                        // so there is a shift of the image. Fixed height was given to prevent this.
                        style={{ minHeight: "260px" }}
                    >
                        <Pivot linkSize="large">
                            <PivotItem
                                headerText={store.i18n.t("formCreateUser.fleetUser.tab.label")}
                                itemIcon="People"
                            >
                                <Stack
                                    horizontal
                                    horizontalAlign="space-between"
                                    tokens={{
                                        childrenGap: "2em",
                                    }}
                                    style={{
                                        paddingTop: theme.spacing.l1,
                                    }}
                                >
                                    <Stack>
                                        <ClearableDropdown
                                            label={store.i18n.t("formCreateUser.fleetRole.label")}
                                            options={Object.values(FleetRole).map((role) => ({
                                                key: role,
                                                text: store.i18n.formatFleetRole(role),
                                            }))}
                                            selectedKey={this.fleetRole}
                                            onChange={this.setFleetRole}
                                            errorMessage={store.i18n.formatFieldValidationState(
                                                store.repositoryUsers.validation.create.getFieldValidationState(
                                                    this.validationId,
                                                    "fleetRole",
                                                ),
                                            )}
                                        />

                                        <TextField
                                            label={store.i18n.t(
                                                "formCreateUser.nfcTokenDescription.label",
                                            )}
                                            {...doubleBindNullableStringEmptyAsNull<FormCreateUser>(
                                                this,
                                                "nfcTokenDescription",
                                                () => this.triggerValidation(),
                                            )}
                                            errorMessage={store.i18n.formatFieldValidationState(
                                                store.repositoryUsers.validation.create.getFieldValidationState(
                                                    this.validationId,
                                                    "nfcTokenDescription",
                                                ),
                                            )}
                                        />
                                        <TextField
                                            label={store.i18n.t("formCreateUser.nfcToken.label")}
                                            {...doubleBindNullableStringEmptyAsNull<FormCreateUser>(
                                                this,
                                                "nfcToken",
                                                () => {
                                                    if (this.nfcToken) {
                                                        this.nfcToken = this.nfcToken
                                                            .replace(/:/g, "")
                                                            .match(/.{1,2}/g)!
                                                            .join(":");
                                                    }
                                                    this.triggerValidation();
                                                },
                                            )}
                                            errorMessage={store.i18n.formatFieldValidationState(
                                                store.repositoryUsers.validation.create.getFieldValidationState(
                                                    this.validationId,
                                                    "nfcToken",
                                                ),
                                            )}
                                        />
                                    </Stack>
                                    <Stack>
                                        <SearchableDropdown
                                            formatEntity={(shift: Shift) =>
                                                store.i18n.formatShift(shift)
                                            }
                                            repository={store.repositoryShifts}
                                            query={{ pageSize: defaultPageSize }}
                                            label={store.i18n.t("formCreateUser.shift.label")}
                                            onChange={this.setShift}
                                            selectedKey={this.shiftId}
                                            errorMessage={store.i18n.formatFieldValidationState(
                                                store.repositoryUsers.validation.create.getFieldValidationState(
                                                    this.validationId,
                                                    "shiftId",
                                                ),
                                            )}
                                            clearable
                                        />
                                        <SearchableDropdown
                                            formatEntity={(site: Site) =>
                                                store.i18n.formatSite(site)
                                            }
                                            repository={store.repositorySites}
                                            query={{ pageSize: defaultPageSize }}
                                            label={store.i18n.t("formCreateUser.site.label")}
                                            onChange={this.setSite}
                                            selectedKey={this.siteId}
                                            errorMessage={store.i18n.formatFieldValidationState(
                                                store.repositoryUsers.validation.create.getFieldValidationState(
                                                    this.validationId,
                                                    "siteId",
                                                ),
                                            )}
                                            clearable
                                        />
                                        <DatePicker
                                            formatDate={this.onFormatDate}
                                            placeholder={store.i18n.t(
                                                "formCreateUser.licenseExpiryDate.placeholder",
                                            )}
                                            label={store.i18n.t(
                                                "formCreateUser.licenseExpiryDate.label",
                                            )}
                                            value={this.licenseExpiryDate}
                                            onSelectDate={this.setLicenseExpiryDate}
                                        />
                                    </Stack>
                                    <Stack>
                                        <Toggle
                                            label={store.i18n.t(
                                                "formCreateUser.expiryLogout.label",
                                            )}
                                            checked={this.expiryLogout}
                                            onChange={this.setExpiryLogout}
                                        />
                                    </Stack>
                                </Stack>
                            </PivotItem>
                            <PivotItem
                                headerText={store.i18n.t("formCreateUser.managementUser.tab.label")}
                                itemIcon="ContactCard"
                            >
                                <Stack
                                    horizontal
                                    horizontalAlign="space-between"
                                    tokens={{
                                        childrenGap: "2em",
                                    }}
                                    style={{
                                        paddingTop: theme.spacing.l2,
                                        paddingBottom: theme.spacing.l2,
                                    }}
                                >
                                    <Stack>
                                        <ClearableDropdown
                                            label={store.i18n.t(
                                                "formCreateUser.managementRole.label",
                                            )}
                                            options={Object.values(ManagementRole)
                                                .filter((role) =>
                                                    store.serviceOwnUser.user?.managementRole ==
                                                    ManagementRole.SUPER_ADMIN
                                                        ? true
                                                        : role != ManagementRole.SUPER_ADMIN,
                                                )
                                                .map((role) => ({
                                                    key: role,
                                                    text: store.i18n.formatManagementRole(role),
                                                }))}
                                            selectedKey={this.managementRole}
                                            onChange={this.setManagementRole}
                                            errorMessage={store.i18n.formatFieldValidationState(
                                                store.repositoryUsers.validation.create.getFieldValidationState(
                                                    this.validationId,
                                                    "managementRole",
                                                ),
                                            )}
                                        />
                                        <TextField
                                            label={store.i18n.t("formCreateUser.email.label")}
                                            {...doubleBindNullableString<FormCreateUser>(
                                                this,
                                                "email",
                                                () => this.triggerValidation(),
                                            )}
                                            required={!!this.managementRole}
                                            errorMessage={store.i18n.formatFieldValidationState(
                                                store.repositoryUsers.validation.create.getFieldValidationState(
                                                    this.validationId,
                                                    "email",
                                                ),
                                            )}
                                        />
                                    </Stack>
                                    <Stack>
                                        <TextField
                                            label={store.i18n.t("formCreateUser.password.label")}
                                            type="password"
                                            canRevealPassword
                                            {...doubleBindNullableString<FormCreateUser>(
                                                this,
                                                "password",
                                                () => this.triggerValidation(),
                                            )}
                                            required={!!this.managementRole}
                                            errorMessage={store.i18n.formatFieldValidationState(
                                                store.repositoryUsers.validation.create.getFieldValidationState(
                                                    this.validationId,
                                                    "password",
                                                ),
                                            )}
                                        />
                                        <TextField
                                            label={store.i18n.t(
                                                "formCreateUser.passwordRepeat.label",
                                            )}
                                            type="password"
                                            canRevealPassword
                                            {...doubleBindNullableString<FormCreateUser>(
                                                this,
                                                "passwordRepeat",
                                            )}
                                            required={!!this.managementRole}
                                            errorMessage={this.passwordRepeatErrorMessage}
                                        />
                                    </Stack>
                                    <Stack></Stack>
                                </Stack>
                            </PivotItem>
                        </Pivot>
                    </Stack>
                </Stack>

                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreateUser.cancel.text")}
                            onClick={this.showConfirmAndCallDialogCancelCallback}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
                {
                    <ModalConfirmation
                        isOpen={this.cancelConfirmationModalVisible}
                        title={store.i18n.t("modalAbortUpdate.title")}
                        text={store.i18n.t("modalAbortUpdate.description")}
                        onConfirm={this.props.onDialogCancel}
                        onCancel={this.closeCancelConfirmationModalVisible}
                    />
                }
            </form>
        );
    }
}
